import React, { useState, useEffect } from 'react';
import { Download, RefreshCw, FileSpreadsheet, Edit3, Check, X, Sparkles } from 'lucide-react';
import { apiService } from '../services/api';

const ExcelPreviewCard = ({ planId }) => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [selectedCells, setSelectedCells] = useState(new Set()); // Set of "{row_index}-{column}" strings
  const [editMode, setEditMode] = useState('rows'); // 'rows' or 'cells'
  const [instruction, setInstruction] = useState('');
  const [previewId, setPreviewId] = useState(null);
  const [patches, setPatches] = useState([]);
  const [selectedPatches, setSelectedPatches] = useState(new Set()); // Set of patch IDs: "{row_index}-{column}"
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [showEditPanel, setShowEditPanel] = useState(false);
  const [editProgress, setEditProgress] = useState(null); // {progress, message, status, current, total}
  const [wsConnection, setWsConnection] = useState(null);
  const [generateProgress, setGenerateProgress] = useState(null); // {slides_generated, topics_completed, total_topics, message, status}
  const [generateWsConnection, setGenerateWsConnection] = useState(null);

  const apiBaseRest = (process.env.REACT_APP_BACKEND_URL || 'https://propulseapi.brainayan.com').replace(/\/$/, '');
  const wsBaseUrl = (process.env.REACT_APP_BACKEND_URL || 'https://propulseapi.brainayan.com')
    .replace(/^https/, 'wss')
    .replace(/^http/, 'ws')
    .replace(/\/$/, '');

  // Columns to display (Excel-like headers)
  const columns = [
    { key: 'slide_title', label: 'Slide Title', width: 200 },
    { key: 'slide_content', label: 'Content', width: 300 },
    { key: 'detailed_description', label: 'Detailed Description', width: 300 },
    { key: 'image', label: 'Image', width: 150 },
    { key: 'link', label: 'Link', width: 200 },
    { key: 'notes_for_facilitator', label: 'Facilitator Notes', width: 250 },
    { key: 'media_reference', label: 'Media Reference', width: 200 },
  ];

  const fetchSlides = async () => {
    if (!planId) return;
    setLoading(true);
    setError('');
    try {
      const result = await apiService.getExcelSlides(planId);
      if (result.success) {
        setSlides(result.slides || []);
        // Clear selections and preview when slides refresh
        setSelectedRows(new Set());
        setSelectedCells(new Set());
        setPreviewId(null);
        setPatches([]);
        setSelectedPatches(new Set());
      } else {
        // Handle 404 or other errors gracefully
        if (result.error && result.error.includes('not found')) {
          setSlides([]); // Clear slides if plan doesn't exist
          setError(''); // Don't show error for missing plans
        } else {
          setError(result.error || 'Failed to fetch slides');
        }
      }
    } catch (err) {
      // Handle 404 or timeout errors gracefully
      if (err.response && err.response.status === 404) {
        setSlides([]);
        setError(''); // Don't show error for missing plans
      } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        setError('Request timed out. The lesson plan may not exist or the server is busy.');
      } else {
        setError(err.message || 'Network error');
      }
    } finally {
      setLoading(false);
    }
  };

  const generateExcel = () => {
    if (!planId || isGenerating) return;
    setIsGenerating(true);
    setError('');
    setGenerateProgress({ 
      slides_generated: 0, 
      topics_completed: 0, 
      total_topics: 0, 
      message: 'Connecting...', 
      status: 'connecting' 
    });

    // Connect to WebSocket for progress tracking
    const wsUrl = `${wsBaseUrl}/ws/excel-generate/${planId}`;
    console.log('🟢 Excel Generate: Connecting to WebSocket:', wsUrl);
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      console.log('🟢 Excel Generate: WebSocket connected');
      setGenerateProgress(prev => ({ ...prev, message: 'Lesson Plan Generating...', status: 'connected' }));
      const message = {
        action: 'start_generate',
        plan_id: planId
      };
      console.log('🟢 Excel Generate: Sending start_generate message');
      ws.send(JSON.stringify(message));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('🟢 Excel Generate: Received message:', data.type || data.status, data);

        if (data.status === 'completed' || data.type === 'completed') {
          setGenerateProgress({
            slides_generated: data.total_slides || data.slides_generated || 0,
            topics_completed: data.topics_completed || 0,
            total_topics: data.total_topics || 0,
            message: 'Generation complete!',
            status: 'completed'
          });
          // Refresh slides after completion
          fetchSlides();
          setSelectedRows(new Set());
          setSelectedCells(new Set());
          setPreviewId(null);
          setPatches([]);
          setIsGenerating(false);
          setTimeout(() => {
            setGenerateProgress(null);
          }, 2000);
          ws.close();
        } else if (data.type === 'progress') {
          setGenerateProgress({
            slides_generated: data.slides_generated || 0,
            topics_completed: data.topics_completed || 0,
            total_topics: data.total_topics || 0,
            message: data.message || 'Lesson Plan Generating...',
            status: data.status || 'generating',
            current_topic: data.current_topic || ''
          });
        } else if (data.type === 'error') {
          setError(data.message || 'An error occurred during generation');
          setIsGenerating(false);
          setGenerateProgress(null);
          ws.close();
        }
      } catch (err) {
        console.error('🟢 Excel Generate: Error parsing WebSocket message:', err);
      }
    };

    ws.onerror = (error) => {
      console.error('🟢 Excel Generate: WebSocket error:', error);
      setError('Connection error. Please try again.');
      setIsGenerating(false);
      setGenerateProgress(null);
    };

    ws.onclose = (event) => {
      console.log('🟢 Excel Generate: WebSocket closed', { code: event.code, reason: event.reason });
      if (generateProgress?.status !== 'completed' && generateProgress?.status !== 'error') {
        // Only show error if not completed
        if (isGenerating) {
          setError('Connection closed unexpectedly');
          setIsGenerating(false);
          setGenerateProgress(null);
        }
      }
    };

    setGenerateWsConnection(ws);
  };

  const downloadExcel = async () => {
    if (!planId || slides.length === 0) return;
    try {
      const blob = await apiService.downloadExcelFile(planId);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${planId || 'LessonPlan'}_Slides.xlsx`;
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError('Error downloading Excel file');
    }
  };

  const toggleRowSelection = (rowIdx) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(rowIdx)) {
      newSelected.delete(rowIdx);
    } else {
      newSelected.add(rowIdx);
    }
    setSelectedRows(newSelected);
  };

  const toggleSelectAll = () => {
    if (editMode === 'rows') {
      if (selectedRows.size === slides.length) {
        setSelectedRows(new Set());
      } else {
        setSelectedRows(new Set(slides.map((_, idx) => idx)));
      }
    } else {
      // For cells mode, select all cells
      if (selectedCells.size === slides.length * columns.length) {
        setSelectedCells(new Set());
      } else {
        const allCells = new Set();
        slides.forEach((_, rowIdx) => {
          columns.forEach(col => {
            allCells.add(`${rowIdx}-${col.key}`);
          });
        });
        setSelectedCells(allCells);
      }
    }
  };

  const toggleCellSelection = (rowIdx, column) => {
    const cellKey = `${rowIdx}-${column}`;
    const newSelected = new Set(selectedCells);
    if (newSelected.has(cellKey)) {
      newSelected.delete(cellKey);
    } else {
      newSelected.add(cellKey);
    }
    setSelectedCells(newSelected);
  };

  const isCellSelected = (rowIdx, column) => {
    return selectedCells.has(`${rowIdx}-${column}`);
  };

  const previewEdits = () => {
    if (!instruction.trim()) {
      setError('Please enter an instruction');
      return;
    }

    setIsPreviewing(true);
    setError('');
    setEditProgress({ progress: 0, message: 'Connecting...', status: 'connecting' });
    
    let scope;
    
    if (editMode === 'cells') {
      // Cell-level editing
      if (selectedCells.size === 0) {
        setError('Please select at least one cell to edit');
        setIsPreviewing(false);
        setEditProgress(null);
        return;
      }
      
      // Convert selected cells to scope format
      const cells = Array.from(selectedCells).map(cellKey => {
        const parts = cellKey.split('-');
        const rowIdx = parts[0];
        const column = parts.slice(1).join('-'); // Join rest in case column name ever contains '-'
        const rowIndexNum = parseInt(rowIdx, 10);
        if (isNaN(rowIndexNum) || rowIndexNum < 0 || rowIndexNum >= slides.length) {
          return null; // Invalid cell
        }
        return {
          row_index: rowIndexNum,
          column: column
        };
      }).filter(cell => cell !== null); // Remove invalid cells
      
      if (cells.length === 0) {
        setError('No valid cells selected');
        setIsPreviewing(false);
        setEditProgress(null);
        return;
      }
      
      scope = {
        type: 'cells',
        cells: cells
      };
    } else {
      // Row-level editing
      const selectedIndices = Array.from(selectedRows);
      if (selectedIndices.length === 0) {
        setError('Please select at least one row to edit');
        setIsPreviewing(false);
        setEditProgress(null);
        return;
      }
      
      scope = {
        type: selectedIndices.length === slides.length ? 'all' : 'rows',
        row_indices: selectedIndices
      };
    }

    // Connect to WebSocket
    const wsUrl = `${wsBaseUrl}/ws/excel-edit/${planId}`;
    console.log('🔵 Excel Edit: Connecting to WebSocket:', wsUrl);
    console.log('🔵 Excel Edit: Scope:', JSON.stringify(scope, null, 2));
    console.log('🔵 Excel Edit: Instruction:', instruction.substring(0, 100) + '...');
    const ws = new WebSocket(wsUrl);
    
    ws.onopen = () => {
      console.log('🔵 Excel Edit: WebSocket connected');
      setEditProgress({ progress: 5, message: 'Connected, starting edit...', status: 'connected' });
      const message = {
        action: 'start_edit',
        plan_id: planId,
        scope: scope,
        instruction: instruction
      };
      console.log('🔵 Excel Edit: Sending start_edit message:', JSON.stringify(message, null, 2));
      ws.send(JSON.stringify(message));
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log('🔵 Excel Edit: Received WebSocket message:', data.type || data.status, data);
        
        // Check for completion first (it may have type: 'progress' and status: 'completed')
        if (data.status === 'completed' || (data.type === 'progress' && data.status === 'completed')) {
          setEditProgress({
            progress: 100,
            message: data.message || 'Completed!',
            status: 'completed'
          });
          setPreviewId(data.preview_id);
          // Fetch patches from diff endpoint
          fetch(`${apiBaseRest}/api/lp/plan/${planId}/excel/diff?preview_id=${data.preview_id}`)
            .then(res => res.json())
            .then(result => {
              console.log('🔵 Excel Edit: Fetched patches:', result);
              if (result.success) {
                const fetchedPatches = result.patches || [];
                setPatches(fetchedPatches);
                // Initialize all patches as selected by default
                const initialSelected = new Set(
                  fetchedPatches.map(p => `${p.row_index}-${p.column}`)
                );
                setSelectedPatches(initialSelected);
                setShowEditPanel(true);
                console.log('🔵 Excel Edit: Patches set, edit panel shown');
              } else {
                console.error('🔵 Excel Edit: Failed to fetch patches:', result.error);
                setError(result.error || 'Failed to fetch patches');
              }
            })
            .catch(err => {
              console.error('🔵 Excel Edit: Error fetching patches:', err);
              setError('Failed to fetch patches. Please try again.');
            });
          setIsPreviewing(false);
          setTimeout(() => {
            setEditProgress(null);
          }, 2000);
          ws.close();
        } else if (data.type === 'progress') {
          setEditProgress({
            progress: data.progress || 0,
            message: data.message || '',
            status: data.status || 'processing',
            current: data.current || 0,
            total: data.total || 0,
            current_item: data.current_item || ''
          });
        } else if (data.type === 'error') {
          setError(data.message || 'An error occurred');
          setIsPreviewing(false);
          setEditProgress(null);
          ws.close();
        }
      } catch (err) {
        console.error('🔵 Excel Edit: Error parsing WebSocket message:', err);
      }
    };

    ws.onerror = (error) => {
      console.error('🔵 Excel Edit: WebSocket error:', error);
      setError('Connection error. Please try again.');
      setIsPreviewing(false);
      setEditProgress(null);
    };

    ws.onclose = (event) => {
      console.log('🔵 Excel Edit: WebSocket closed', { code: event.code, reason: event.reason, wasClean: event.wasClean });
      if (editProgress?.status !== 'completed') {
        // Only show error if not completed
        if (editProgress?.status !== 'error') {
          setError('Connection closed unexpectedly');
          setIsPreviewing(false);
          setEditProgress(null);
        }
      }
    };

    setWsConnection(ws);
  };

  const togglePatchSelection = (rowIdx, column) => {
    const patchId = `${rowIdx}-${column}`;
    setSelectedPatches(prev => {
      const next = new Set(prev);
      if (next.has(patchId)) {
        next.delete(patchId);
      } else {
        next.add(patchId);
      }
      return next;
    });
  };

  const toggleSelectAllPatches = () => {
    if (selectedPatches.size === patches.length) {
      // Deselect all
      setSelectedPatches(new Set());
    } else {
      // Select all
      const allPatchIds = new Set(patches.map(p => `${p.row_index}-${p.column}`));
      setSelectedPatches(allPatchIds);
    }
  };

  const isPatchSelected = (rowIdx, column) => {
    return selectedPatches.has(`${rowIdx}-${column}`);
  };

  const applyPatches = async () => {
    if (!previewId) {
      setError('No preview available to apply');
      return;
    }

    if (selectedPatches.size === 0) {
      setError('Please select at least one change to apply');
      return;
    }

    setIsApplying(true);
    setError('');
    try {
      // Filter patches to only include selected ones
      const patchesToApply = patches.filter(p => 
        selectedPatches.has(`${p.row_index}-${p.column}`)
      );
      
      const result = await apiService.applyExcelPatches(planId, previewId, patchesToApply);
      
      if (result.success) {
        // Refresh slides after applying
        await fetchSlides();
        setPreviewId(null);
        setPatches([]);
        setSelectedPatches(new Set());
        setInstruction('');
        setSelectedRows(new Set());
        setSelectedCells(new Set());
        setShowEditPanel(false);
      } else {
        setError(result.error || 'Failed to apply patches');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to apply patches');
    } finally {
      setIsApplying(false);
    }
  };

  const cancelPreview = () => {
    setPreviewId(null);
    setPatches([]);
    setSelectedPatches(new Set());
    setShowEditPanel(false);
  };

  const getSelectedCount = () => {
    if (editMode === 'cells') {
      return selectedCells.size;
    }
    return selectedRows.size;
  };

  const getPatchForRow = (rowIdx) => {
    return patches.filter(p => p.row_index === rowIdx);
  };

  const getCellValue = (rowIdx, column) => {
    const rowPatches = getPatchForRow(rowIdx);
    const patch = rowPatches.find(p => p.column === column);
    if (patch) {
      return patch.new_value;
    }
    return slides[rowIdx]?.[column] || '';
  };

  useEffect(() => {
    if (planId) {
      fetchSlides();
    }
    
    // Cleanup WebSocket connections on unmount
    return () => {
      if (wsConnection) {
        wsConnection.close();
      }
      if (generateWsConnection) {
        generateWsConnection.close();
      }
    };
  }, [planId]);

  return (
    <div style={{
      border: '1px solid #334155',
      borderRadius: 12,
      padding: 16,
      background: 'rgba(2,6,23,0.6)',
      marginTop: 16,
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <FileSpreadsheet size={20} color="#3b82f6" />
          <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#e2e8f0', margin: 0 }}>
            Excel Slides Preview
          </h3>
          {slides.length > 0 && (
            <span style={{ fontSize: '12px', color: '#94a3b8' }}>
              ({slides.length} {slides.length === 1 ? 'slide' : 'slides'})
            </span>
          )}
          {getSelectedCount() > 0 && (
            <span style={{ fontSize: '12px', color: '#3b82f6' }}>
              ({getSelectedCount()} {editMode === 'cells' ? 'cells' : 'rows'} selected)
            </span>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            onClick={fetchSlides}
            disabled={loading}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid #334155',
              background: loading ? 'rgba(51,65,85,0.6)' : 'rgba(51,65,85,0.3)',
              color: '#e2e8f0',
              fontSize: '12px',
              cursor: loading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <RefreshCw size={14} />
            Refresh
          </button>
          <button
            onClick={generateExcel}
            disabled={isGenerating}
            style={{
              padding: '6px 12px',
              borderRadius: 6,
              border: '1px solid rgba(34,197,94,0.5)',
              background: isGenerating ? 'rgba(34,197,94,0.3)' : 'rgba(34,197,94,0.6)',
              color: '#d1fae5',
              fontSize: '12px',
              fontWeight: 500,
              cursor: isGenerating ? 'not-allowed' : 'pointer',
            }}
          >
            {isGenerating ? 'Generating...' : 'Generate Excel'}
          </button>
          {slides.length > 0 && (
            <>
              <button
                onClick={() => {
                  if (showEditPanel) {
                    // When closing, clear preview but keep selections
                    setPreviewId(null);
                    setPatches([]);
                    setSelectedPatches(new Set());
                  }
                  setShowEditPanel(!showEditPanel);
                }}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(168,85,247,0.5)',
                  background: showEditPanel ? 'rgba(168,85,247,0.8)' : 'rgba(168,85,247,0.6)',
                  color: '#e9d5ff',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Edit3 size={14} />
                {showEditPanel ? 'Hide Edit' : 'Edit'}
              </button>
              <button
                onClick={downloadExcel}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: '1px solid rgba(59,130,246,0.5)',
                  background: 'rgba(59,130,246,0.6)',
                  color: '#bfdbfe',
                  fontSize: '12px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Download size={14} />
                Download
              </button>
            </>
          )}
        </div>
      </div>

      {/* Generation Progress Bar */}
      {generateProgress && (
        <div style={{
          padding: '12px',
          borderRadius: 8,
          background: 'rgba(15,23,42,0.6)',
          border: '1px solid #334155',
          marginBottom: 12,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 8,
          }}>
            <span style={{
              fontSize: '13px',
              color: generateProgress.status === 'completed' ? '#22c55e' 
                    : generateProgress.status === 'error' ? '#ef4444'
                    : '#3b82f6',
              fontWeight: 600,
            }}>
              {generateProgress.message}
            </span>
            <span style={{
              fontSize: '14px',
              color: '#e2e8f0',
              fontWeight: 600,
            }}>
              Total slides generated: {generateProgress.slides_generated}
            </span>
          </div>
          {generateProgress.total_topics > 0 && (
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 6,
            }}>
              <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                Topics: {generateProgress.topics_completed}/{generateProgress.total_topics}
              </span>
              {generateProgress.current_topic && (
                <span style={{ fontSize: '11px', color: '#64748b' }}>
                  Processing: {generateProgress.current_topic}
                </span>
              )}
            </div>
          )}
          <div style={{
            width: '100%',
            height: 8,
            borderRadius: 4,
            background: 'rgba(51,65,85,0.5)',
            overflow: 'hidden',
          }}>
            <div style={{
              width: generateProgress.total_topics > 0 
                ? `${(generateProgress.topics_completed / generateProgress.total_topics) * 100}%`
                : generateProgress.status === 'completed' ? '100%' : '10%',
              height: '100%',
              background: generateProgress.status === 'completed' ? '#22c55e'
                        : generateProgress.status === 'error' ? '#ef4444'
                        : 'linear-gradient(90deg, #22c55e, #3b82f6)',
              transition: 'width 0.3s ease',
              borderRadius: 4,
            }} />
          </div>
        </div>
      )}

      {/* Edit Panel */}
      {showEditPanel && slides.length > 0 && (
        <div style={{
          padding: '12px',
          borderRadius: 8,
          background: 'rgba(15,23,42,0.6)',
          border: '1px solid #334155',
          marginBottom: 12,
        }}>
          {/* Mode Toggle */}
          <div style={{ marginBottom: 12, display: 'flex', gap: 8 }}>
            <button
              onClick={() => {
                setEditMode('rows');
                setSelectedCells(new Set());
              }}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #334155',
                background: editMode === 'rows' ? 'rgba(59,130,246,0.6)' : 'rgba(51,65,85,0.3)',
                color: editMode === 'rows' ? '#bfdbfe' : '#e2e8f0',
                fontSize: '11px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Row Selection
            </button>
            <button
              onClick={() => {
                setEditMode('cells');
                setSelectedRows(new Set());
              }}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: '1px solid #334155',
                background: editMode === 'cells' ? 'rgba(59,130,246,0.6)' : 'rgba(51,65,85,0.3)',
                color: editMode === 'cells' ? '#bfdbfe' : '#e2e8f0',
                fontSize: '11px',
                fontWeight: 500,
                cursor: 'pointer',
              }}
            >
              Cell Selection
            </button>
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', color: '#e2e8f0', fontSize: '12px', fontWeight: 500, marginBottom: 6 }}>
              Edit Instruction:
            </label>
            <textarea
              value={instruction}
              onChange={(e) => setInstruction(e.target.value)}
              placeholder="e.g., Make all titles more concise, Add timings to facilitator notes, Expand activities with steps..."
              style={{
                width: '100%',
                minHeight: '60px',
                padding: '8px',
                borderRadius: 6,
                border: '1px solid #334155',
                background: '#0b1324',
                color: '#e2e8f0',
                fontSize: '12px',
                fontFamily: 'inherit',
                resize: 'vertical',
              }}
            />
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button
              onClick={previewEdits}
              disabled={isPreviewing || !instruction.trim() || getSelectedCount() === 0}
              style={{
                padding: '8px 16px',
                borderRadius: 6,
                border: '1px solid rgba(168,85,247,0.5)',
                background: isPreviewing || !instruction.trim() || getSelectedCount() === 0
                  ? 'rgba(168,85,247,0.3)' : 'rgba(168,85,247,0.7)',
                color: '#e9d5ff',
                fontSize: '12px',
                fontWeight: 500,
                cursor: isPreviewing || !instruction.trim() || getSelectedCount() === 0
                  ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Sparkles size={14} />
              {isPreviewing ? 'Generating Preview...' : 'Preview Edits'}
            </button>
            {/* Progress Bar */}
            {editProgress && (
              <div style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 6,
                background: 'rgba(15,23,42,0.8)',
                border: '1px solid #334155',
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 4,
                }}>
                  <span style={{
                    fontSize: '11px',
                    color: editProgress.status === 'completed' ? '#22c55e' 
                          : editProgress.status === 'error' ? '#ef4444'
                          : '#3b82f6',
                    fontWeight: 500,
                  }}>
                    {editProgress.message}
                  </span>
                  {editProgress.total > 0 && (
                    <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                      {editProgress.current}/{editProgress.total}
                    </span>
                  )}
                </div>
                <div style={{
                  width: '100%',
                  height: 6,
                  borderRadius: 3,
                  background: 'rgba(51,65,85,0.5)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${editProgress.progress}%`,
                    height: '100%',
                    background: editProgress.status === 'completed' ? '#22c55e'
                              : editProgress.status === 'error' ? '#ef4444'
                              : 'linear-gradient(90deg, #3b82f6, #8b5cf6)',
                    transition: 'width 0.3s ease',
                    borderRadius: 3,
                  }} />
                </div>
                {editProgress.current_item && (
                  <div style={{
                    fontSize: '10px',
                    color: '#64748b',
                    marginTop: 4,
                  }}>
                    Processing: {editProgress.current_item}
                  </div>
                )}
              </div>
            )}
            {previewId && patches.length > 0 && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={toggleSelectAllPatches}
                    disabled={isApplying}
                    style={{
                      padding: '6px 12px',
                      borderRadius: 6,
                      border: '1px solid #334155',
                      background: 'rgba(51,65,85,0.3)',
                      color: '#e2e8f0',
                      fontSize: '11px',
                      fontWeight: 500,
                      cursor: isApplying ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {selectedPatches.size === patches.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <span style={{ fontSize: '11px', color: '#94a3b8' }}>
                    {selectedPatches.size} of {patches.length} selected
                  </span>
                </div>
                <button
                  onClick={applyPatches}
                  disabled={isApplying || selectedPatches.size === 0}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: '1px solid rgba(34,197,94,0.5)',
                    background: isApplying || selectedPatches.size === 0 
                      ? 'rgba(34,197,94,0.3)' 
                      : 'rgba(34,197,94,0.7)',
                    color: '#d1fae5',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: isApplying || selectedPatches.size === 0 ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <Check size={14} />
                  {isApplying ? 'Applying...' : `Apply ${selectedPatches.size} Change${selectedPatches.size !== 1 ? 's' : ''}`}
                </button>
                <button
                  onClick={cancelPreview}
                  disabled={isApplying}
                  style={{
                    padding: '8px 16px',
                    borderRadius: 6,
                    border: '1px solid rgba(239,68,68,0.5)',
                    background: 'rgba(239,68,68,0.3)',
                    color: '#fca5a5',
                    fontSize: '12px',
                    fontWeight: 500,
                    cursor: isApplying ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 6,
                  }}
                >
                  <X size={14} />
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div style={{
          padding: '8px 12px',
          borderRadius: 6,
          background: 'rgba(239,68,68,0.1)',
          border: '1px solid rgba(239,68,68,0.3)',
          color: '#fca5a5',
          fontSize: '12px',
          marginBottom: 12,
        }}>
          {error}
        </div>
      )}

      {/* Excel-like Table */}
      {loading ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#94a3b8',
          fontSize: '13px',
        }}>
          Loading slides...
        </div>
      ) : slides.length === 0 ? (
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#94a3b8',
          fontSize: '13px',
        }}>
          No slides generated yet. Click "Generate Excel" to create slides.
        </div>
      ) : (
        <div style={{
          border: '1px solid #334155',
          borderRadius: 8,
          overflow: 'hidden',
          background: '#0b1324',
          maxHeight: 400,
          display: 'flex',
          flexDirection: 'column',
        }}>
          {/* Scrollable Container */}
          <div style={{
            overflowX: 'auto',
            overflowY: 'auto',
            flex: 1,
          }}>
            {/* Excel-like Header Row */}
            <div style={{
              display: 'flex',
              borderBottom: '2px solid #334155',
              background: '#1e293b',
              position: 'sticky',
              top: 0,
              zIndex: 10,
            }}>
              {/* Checkbox Column Header */}
              {editMode === 'rows' && (
                <div style={{
                  width: 40,
                  minWidth: 40,
                  padding: '8px',
                  borderRight: '1px solid #334155',
                  background: '#1e293b',
                  color: '#94a3b8',
                  fontSize: '11px',
                  fontWeight: 600,
                  textAlign: 'center',
                  position: 'sticky',
                  left: 0,
                  zIndex: 11,
                  cursor: 'pointer',
                }} onClick={toggleSelectAll}>
                  <input
                    type="checkbox"
                    checked={selectedRows.size === slides.length && slides.length > 0}
                    onChange={toggleSelectAll}
                    style={{ cursor: 'pointer' }}
                  />
                </div>
              )}
              {/* Row Number Column Header */}
              <div style={{
                width: 50,
                minWidth: 50,
                padding: '8px',
                borderRight: '1px solid #334155',
                background: '#1e293b',
                color: '#94a3b8',
                fontSize: '11px',
                fontWeight: 600,
                textAlign: 'center',
                position: 'sticky',
                left: editMode === 'rows' ? 40 : 0,
                zIndex: 11,
              }}>
                #
              </div>
              {/* Column Headers */}
              {columns.map((col, idx) => (
                <div
                  key={col.key}
                  style={{
                    width: col.width,
                    minWidth: col.width,
                    padding: '8px',
                    borderRight: idx < columns.length - 1 ? '1px solid #334155' : 'none',
                    background: '#1e293b',
                    color: '#e2e8f0',
                    fontSize: '11px',
                    fontWeight: 600,
                    textAlign: 'left',
                  }}
                >
                  {String.fromCharCode(65 + idx)}: {col.label}
                </div>
              ))}
            </div>

            {/* Scrollable Body */}
            <div>
              {slides.map((slide, rowIdx) => {
                const isSelected = selectedRows.has(rowIdx);
                const rowPatches = getPatchForRow(rowIdx);
                const hasChanges = rowPatches.length > 0;

                return (
                  <div
                    key={rowIdx}
                    style={{
                      display: 'flex',
                      borderBottom: '1px solid #334155',
                      background: hasChanges
                        ? 'rgba(168,85,247,0.1)'
                        : rowIdx % 2 === 0 ? 'rgba(15,23,42,0.3)' : 'rgba(15,23,42,0.2)',
                    }}
                  >
                    {/* Checkbox */}
                    {editMode === 'rows' && (
                      <div style={{
                        width: 40,
                        minWidth: 40,
                        padding: '8px',
                        borderRight: '1px solid #334155',
                        background: hasChanges
                          ? 'rgba(168,85,247,0.1)'
                          : rowIdx % 2 === 0 ? 'rgba(15,23,42,0.3)' : 'rgba(15,23,42,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'sticky',
                        left: 0,
                        zIndex: 5,
                      }}>
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRowSelection(rowIdx)}
                          style={{ cursor: 'pointer' }}
                        />
                      </div>
                    )}
                    {/* Row Number */}
                    <div style={{
                      width: 50,
                      minWidth: 50,
                      padding: '8px',
                      borderRight: '1px solid #334155',
                      background: hasChanges
                        ? 'rgba(168,85,247,0.1)'
                        : rowIdx % 2 === 0 ? 'rgba(15,23,42,0.3)' : 'rgba(15,23,42,0.2)',
                      color: '#64748b',
                      fontSize: '11px',
                      textAlign: 'center',
                      position: 'sticky',
                      left: editMode === 'rows' ? 40 : 0,
                      zIndex: 5,
                    }}>
                      {rowIdx + 1}
                    </div>
                    {/* Data Cells */}
                    {columns.map((col, colIdx) => {
                      const originalValue = slide[col.key] || '';
                      const newValue = getCellValue(rowIdx, col.key);
                      const isChanged = newValue !== originalValue && newValue !== '';
                      const isSelected = editMode === 'cells' && isCellSelected(rowIdx, col.key);
                      const patchSelected = previewId && isChanged && isPatchSelected(rowIdx, col.key);

                      return (
                        <div
                          key={col.key}
                          onClick={() => {
                            if (editMode === 'cells') {
                              toggleCellSelection(rowIdx, col.key);
                            }
                          }}
                          style={{
                            width: col.width,
                            minWidth: col.width,
                            padding: '8px',
                            borderRight: colIdx < columns.length - 1 ? '1px solid #334155' : 'none',
                            color: isChanged ? '#e9d5ff' : '#cbd5e1',
                            fontSize: '12px',
                            wordBreak: 'break-word',
                            whiteSpace: 'pre-wrap',
                            position: 'relative',
                            cursor: editMode === 'cells' ? 'pointer' : 'default',
                            background: isSelected 
                              ? 'rgba(59,130,246,0.2)' 
                              : isChanged 
                                ? 'rgba(168,85,247,0.05)' 
                                : rowIdx % 2 === 0 ? 'rgba(15,23,42,0.3)' : 'rgba(15,23,42,0.2)',
                            border: isSelected ? '2px solid rgba(59,130,246,0.6)' : 'none',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 6,
                          }}
                          title={isChanged ? `Changed: ${originalValue} → ${newValue}` : originalValue}
                        >
                          {/* Patch Selection Checkbox (only shown when preview is active and cell has changes) */}
                          {previewId && isChanged && (
                            <input
                              type="checkbox"
                              checked={patchSelected}
                              onChange={(e) => {
                                e.stopPropagation();
                                togglePatchSelection(rowIdx, col.key);
                              }}
                              onClick={(e) => e.stopPropagation()}
                              style={{
                                cursor: 'pointer',
                                marginTop: '2px',
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <div style={{ flex: 1 }}>
                            {isChanged ? (
                              <div>
                                <div style={{ 
                                  textDecoration: patchSelected === false ? 'line-through' : 'none',
                                  opacity: patchSelected === false ? 0.4 : 0.6, 
                                  color: '#94a3b8',
                                  fontSize: '11px',
                                  marginBottom: 2,
                                }}>
                                  {originalValue || '-'}
                                </div>
                                <div style={{ 
                                  color: patchSelected === false ? '#64748b' : '#e9d5ff', 
                                  fontWeight: patchSelected === false ? 400 : 500,
                                  opacity: patchSelected === false ? 0.5 : 1,
                                }}>
                                  {newValue}
                                </div>
                              </div>
                            ) : (
                              originalValue || '-'
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelPreviewCard;
